import React, { useState } from 'react';

import {
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_SOIL_INPUT,
} from 'constants/uiConstants';

/* ── Layout constants ────────────────────────────────────────────────────────
   Same geometry as NutritionProgressChart for visual consistency.
   ─────────────────────────────────────────────────────────────────────────── */

const CX = 80;
const CY = 80;
const R_OUTER = 72;
const R_INNER = 44;
const VIEW_SIZE = 160;

/**
 * Minimum arc (as % of 360°) so that tiny categories are still visible.
 * 4° ≈ 1.11 % of the circle.
 */
const MIN_PCT = (4 / 360) * 100;

/* ── Category colours ─────────────────────────────────────────────────────── */

/**
 * Distinct palette per SummaryEntityCategory.
 * Exported so CategorySpendPanel can use the same colours for its table dots.
 */
export const SPEND_CATEGORY_COLORS: Record<string, string> = {
  [SUMMARY_ENTITY_SEED]: '#22c55e',
  [SUMMARY_ENTITY_SOIL_INPUT]: '#f59e0b',
  [SUMMARY_ENTITY_PLANT_PROTECTION]: '#f43f5e',
  [SUMMARY_ENTITY_SEED_DRESS]: '#8b5cf6',
  [SUMMARY_ENTITY_BIOSTIMULANT]: '#14b8a6',
  [SUMMARY_ENTITY_OPERATION]: '#0ea5e9',
};

/**
 * Abbreviated labels for the donut centre — the inner radius limits width
 * to ~88 SVG units, so "Plant Protection" needs shortening.
 */
const SPEND_CENTER_LABELS: Record<string, string> = {
  [SUMMARY_ENTITY_SEED]: 'Seed',
  [SUMMARY_ENTITY_SOIL_INPUT]: 'Soil Input',
  [SUMMARY_ENTITY_PLANT_PROTECTION]: 'Plant Prot.',
  [SUMMARY_ENTITY_SEED_DRESS]: 'Seed Treat.',
  [SUMMARY_ENTITY_BIOSTIMULANT]: 'Biostim.',
  [SUMMARY_ENTITY_OPERATION]: 'Operation',
};

/* ── Format helpers ──────────────────────────────────────────────────────── */

function formatDollar(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(2)}k`;
  return `$${v.toFixed(2)}`;
}

/* ── SVG arc helpers ─────────────────────────────────────────────────────── */

function polarToXY(
  cx: number,
  cy: number,
  r: number,
  angleRad: number
): { x: number; y: number } {
  return {
    x: cx + r * Math.cos(angleRad - Math.PI / 2),
    y: cy + r * Math.sin(angleRad - Math.PI / 2),
  };
}

function donutPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number
): string {
  const sweep = Math.min(endAngle - startAngle, 2 * Math.PI - 0.0001);
  const end = startAngle + sweep;
  const o1 = polarToXY(cx, cy, outerR, startAngle);
  const o2 = polarToXY(cx, cy, outerR, end);
  const i1 = polarToXY(cx, cy, innerR, end);
  const i2 = polarToXY(cx, cy, innerR, startAngle);
  const large = sweep > Math.PI ? 1 : 0;
  const f = (n: number) => n.toFixed(3);
  return [
    `M ${f(o1.x)} ${f(o1.y)}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${f(o2.x)} ${f(o2.y)}`,
    `L ${f(i1.x)} ${f(i1.y)}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${f(i2.x)} ${f(i2.y)}`,
    'Z',
  ].join(' ');
}

/* ── Slice building ──────────────────────────────────────────────────────── */

export interface SpendPieDatum {
  category: string;
  planTotalSpend: number;
  actualTotalSpend: number;
}

interface SliceData {
  datum: SpendPieDatum;
  /** Share of total Plan spend (%) — defines this wedge's angular width. */
  pct: number;
  /** Display share after MIN_PCT boosting. */
  visualPct: number;
  /** Actual / Plan for this category, or null when there's no Plan to measure against. */
  ratio: number | null;
  isOver: boolean;
}

function buildSlices(data: SpendPieDatum[]): SliceData[] {
  // Wedge angle is driven by Plan alone — a category with no Plan spend has
  // no proportional share to draw, so it's excluded from the donut entirely
  // (it's still visible in the table).
  const totalPlan = data.reduce((s, d) => s + d.planTotalSpend, 0);
  if (totalPlan <= 0) return [];

  let debt = 0;
  const items: SliceData[] = data
    .filter((d) => d.planTotalSpend > 0)
    .map((datum) => {
      const pct = (datum.planTotalSpend / totalPlan) * 100;
      const ratio =
        datum.actualTotalSpend > 0
          ? datum.actualTotalSpend / datum.planTotalSpend
          : null;
      const isOver = ratio !== null && ratio > 1;
      if (pct < MIN_PCT) {
        debt += MIN_PCT - pct;
        return { datum, pct, visualPct: MIN_PCT, ratio, isOver };
      }
      return { datum, pct, visualPct: pct, ratio, isOver };
    });

  // Drain debt from the largest slice so the total visual % stays at 100
  if (debt > 0.001) {
    let li = 0;
    items.forEach((e, i) => {
      if (e.visualPct > items[li].visualPct) li = i;
    });
    items[li] = {
      ...items[li],
      visualPct: Math.max(MIN_PCT, items[li].visualPct - debt),
    };
  }

  return items;
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface SpendPieChartProps {
  data: SpendPieDatum[];
  /** Controlled hover — lets a table row above the chart drive the same highlight as hovering a slice directly. Falls back to internal state when omitted. */
  hoveredCategory?: string | null;
  onHoverCategoryChange?: (category: string | null) => void;
}

/**
 * SVG donut chart showing Plan spend distribution across categories, with
 * Actual progress shaded within each wedge.
 *
 * - Wedge angle = category's share of total Plan spend (unaffected by
 *   Actual) — each category uses its colour from SPEND_CATEGORY_COLORS.
 * - When Actual > 0 and ≤ Plan for that category, the wedge splits radially:
 *   an inner solid band shows Actual / Plan, an outer faded band (same
 *   colour, reduced opacity) shows what's left to spend.
 * - When Actual has no data at all for a category, the wedge is left as a
 *   single flat colour — no progress split is drawn, so a category with no
 *   Actual tracking yet doesn't add visual noise.
 * - When Actual exceeds Plan, the whole wedge switches to a warning colour
 *   instead of a progress split — there's nowhere left in the ring to show
 *   "125%", so the colour itself is the signifier.
 * - Any non-zero slice is guaranteed ≥ 4° so it stays visible.
 * - Hovering a slice dims others and shows the category label and its
 *   spend progress in the donut centre.
 */
const SpendPieChart: React.FC<SpendPieChartProps> = ({
  data,
  hoveredCategory,
  onHoverCategoryChange,
}) => {
  const [internalHoverCategory, setInternalHoverCategory] = useState<
    string | null
  >(null);
  const hoverCategory =
    hoveredCategory !== undefined ? hoveredCategory : internalHoverCategory;
  const setHoverCategory = onHoverCategoryChange ?? setInternalHoverCategory;

  const sliceData = buildSlices(data);
  if (sliceData.length === 0) return null;

  const totalVisual = sliceData.reduce((s, e) => s + e.visualPct, 0);

  let cumulative = 0;
  const slices = sliceData.map((sd) => {
    const sweep = (sd.visualPct / totalVisual) * 2 * Math.PI;
    const start = cumulative;
    cumulative += sweep;
    return { ...sd, start, end: cumulative };
  });

  const hovered = sliceData.find((e) => e.datum.category === hoverCategory);

  return (
    <svg
      viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
      className="w-full"
      role="img"
      aria-label="Category spend distribution chart"
    >
      {slices.map((s) => {
        const color = SPEND_CATEGORY_COLORS[s.datum.category] ?? '#9ca3af';
        const dimmed = !!hoverCategory && hoverCategory !== s.datum.category;
        const isHovered = hoverCategory === s.datum.category;

        if (s.isOver) {
          return (
            <path
              key={s.datum.category}
              d={donutPath(CX, CY, R_OUTER, R_INNER, s.start, s.end)}
              fill="var(--color-danger)"
              opacity={dimmed ? 0.25 : 1}
              stroke="white"
              strokeWidth={isHovered ? 2.5 : 0.5}
              onMouseEnter={() => setHoverCategory(s.datum.category)}
              onMouseLeave={() => setHoverCategory(null)}
              className="cursor-pointer transition-opacity duration-150"
            />
          );
        }

        if (s.ratio !== null) {
          const splitAngle = s.start + (s.end - s.start) * s.ratio;
          return (
            <g key={s.datum.category}>
              <path
                d={donutPath(CX, CY, R_OUTER, R_INNER, s.start, s.end)}
                fill={color}
                opacity={dimmed ? 0.15 : 0.3}
                onMouseEnter={() => setHoverCategory(s.datum.category)}
                onMouseLeave={() => setHoverCategory(null)}
                className="cursor-pointer transition-opacity duration-150"
              />
              <path
                d={donutPath(CX, CY, R_OUTER, R_INNER, s.start, splitAngle)}
                fill={color}
                opacity={dimmed ? 0.25 : 1}
                stroke="white"
                strokeWidth={isHovered ? 2.5 : 0.5}
                pointerEvents="none"
              />
            </g>
          );
        }

        return (
          <path
            key={s.datum.category}
            d={donutPath(CX, CY, R_OUTER, R_INNER, s.start, s.end)}
            fill={color}
            opacity={dimmed ? 0.25 : 1}
            stroke="white"
            strokeWidth={isHovered ? 2.5 : 0.5}
            onMouseEnter={() => setHoverCategory(s.datum.category)}
            onMouseLeave={() => setHoverCategory(null)}
            className="cursor-pointer transition-opacity duration-150"
          />
        );
      })}

      {hovered ? (
        <>
          <text
            x={CX}
            y={CY - 7}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="7"
            fontWeight="600"
            fill="var(--color-charcoal)"
          >
            {SPEND_CENTER_LABELS[hovered.datum.category] ??
              hovered.datum.category}
          </text>
          <text
            x={CX}
            y={CY + 8}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="8"
            fontWeight="600"
            fill={
              hovered.isOver ? 'var(--color-danger)' : 'var(--color-charcoal)'
            }
          >
            {hovered.ratio === null
              ? `${hovered.pct.toFixed(1)}%`
              : `${formatDollar(hovered.datum.actualTotalSpend)} / ${formatDollar(hovered.datum.planTotalSpend)}`}
          </text>
        </>
      ) : (
        <>
          <text
            x={CX}
            y={CY - 5}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="7"
            fill="var(--color-secondary-dark)"
          >
            Hover to see
          </text>
          <text
            x={CX}
            y={CY + 8}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="7"
            fill="var(--color-secondary-dark)"
          >
            plan vs actual
          </text>
        </>
      )}
    </svg>
  );
};

export default SpendPieChart;
